/**
 * Skill `/` 与 Knowledge `@` 共用的触发态机：
 * 检测触发符 → index → query → open/close → cleanup → focus 重检 → 虚拟锚点
 */
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useLatest, useMemoizedFn } from 'ahooks';
import { hideVirtualAnchor, updateVirtualAnchorAtChar } from './caretUtils';
import { extractMentionQuery, replaceTriggerOrInsertAt, resolveMentionTriggerIndex } from './mentionText';
import { isSerializedMentionIndex } from '../utils/inlineMentionRanges';
import { isOffsetInsideDomMention } from '../mentionEditor/domMentionBounds';
import { getSelectionOffsets } from '../mentionEditor/editorDom';
import { buildKnowledgeMentionKey } from '../mentionEditor/mentionKeyUtils';
import type { IInsertMentionOptions, IMentionEditorSurface } from '../mentionEditor/types';
import { isMentionEditorSurface } from '../mentionEditor/types';
import { parseMentionBodyFromLabel } from '../mentionEditor/mentionTokenDom';

export type TInputSurface = HTMLTextAreaElement | IMentionEditorSurface;

export type TMentionButtonReason = 'button';

/** cleanupTriggerText 可选行为：button 模式插入但不关下拉、不覆盖已有 `/` */
export interface ICleanupTriggerBehavior {
  closeDropdown?: boolean;
  insertAtSelection?: boolean;
}

export interface IUseMentionTriggerOptions {
  triggerChar: string;
  charOpenReason: string;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  textareaRef: React.RefObject<TInputSurface | null>;
  anchorDataAttr: string;
  clearIndexOnClose?: boolean;
}

export interface IUseMentionTriggerResult {
  dropdownOpen: boolean;
  mentionQuery: string;
  mentionOpenReason: TMentionButtonReason | string;
  mentionAnchorRef: React.RefObject<HTMLElement | null>;
  onDropdownOpenChange: (open: boolean) => void;
  /** 按钮 / hover 显式打开：切到 button 原因并清 char 触发索引 */
  openDropdownAsButton: () => void;
  wrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  isCharModeOpen: boolean;
  cleanupTriggerText: (
    insertLabel?: string,
    insertOptions?: IInsertMentionOptions,
    behavior?: ICleanupTriggerBehavior,
  ) => void;
  handleTextareaFocus: () => void;
}

export function useMentionTrigger({
  triggerChar,
  charOpenReason,
  inputValue,
  setInputValue,
  textareaRef,
  anchorDataAttr,
  clearIndexOnClose = false,
}: IUseMentionTriggerOptions): IUseMentionTriggerResult {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mentionOpenReason, setMentionOpenReason] =
    useState<TMentionButtonReason | string>('button');
  const [mentionQuery, setMentionQuery] = useState('');
  const triggerIndexRef = useRef<number>(-1);
  const programmaticFocusRef = useRef(false);

  const anchorRef = useRef<HTMLElement | null>(null);
  if (!anchorRef.current && typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.setAttribute(anchorDataAttr, 'true');
    document.body.appendChild(el);
    anchorRef.current = el;
  }

  const openLatest = useLatest(dropdownOpen);
  const inputLatest = useLatest(inputValue);
  const pendingCursorRef = useRef<number | null>(null);

  const updateAnchorPosition = useCallback(
    (charIndex: number) => {
      const textarea = textareaRef.current;
      const anchor = anchorRef.current;
      if (!textarea || !anchor) return;
      updateVirtualAnchorAtChar(textarea, anchor, charIndex);
    },
    [textareaRef],
  );

  useEffect(() => {
    if (dropdownOpen) return;
    if (anchorRef.current) {
      hideVirtualAnchor(anchorRef.current);
    }
  }, [dropdownOpen]);

  const resolveChangeContext = useMemoizedFn(
    (
      value: string,
      cursorPos: number,
    ): { value: string; cursorPos: number; } => {
      const surface = textareaRef.current;
      if (!isMentionEditorSurface(surface)) {
        return { value, cursorPos };
      }
      const root = surface.getRootElement();
      if (!root) {
        return { value, cursorPos };
      }
      return {
        value: surface.value || value,
        cursorPos: getSelectionOffsets(root).start,
      };
    },
  );

  const tryOpenByTrigger = useMemoizedFn(
    (value: string, cursorPos: number): boolean => {
      const resolved = resolveChangeContext(value, cursorPos);
      const resolvedValue = resolved.value;
      const resolvedCursor = resolved.cursorPos;

      if (resolvedCursor === 0 || !resolvedValue) return false;
      if (resolvedValue.charAt(resolvedCursor - 1) !== triggerChar) return false;

      const idx = resolvedCursor - 1;
      const surface = textareaRef.current;
      // 编辑态：只认 DOM token；纯文本里的 `/` `@` 不算已有 mention
      if (isMentionEditorSurface(surface)) {
        const root = surface.getRootElement();
        if (root && isOffsetInsideDomMention(root, idx)) {
          return false;
        }
      } else if (isSerializedMentionIndex(resolvedValue, idx, resolvedCursor)) {
        return false;
      }
      // 已在同一 @ 位打开 → 交给 wrapTextareaChange 更新 query
      if (
        openLatest.current
        && triggerIndexRef.current === idx
        && resolvedValue.charAt(idx) === triggerChar
      ) {
        return false;
      }

      triggerIndexRef.current = idx;
      setMentionOpenReason(charOpenReason);
      setMentionQuery('');
      updateAnchorPosition(idx);
      requestAnimationFrame(() => {
        updateAnchorPosition(idx);
        setDropdownOpen(true);
      });
      return true;
    },
  );

  const wrapTextareaChange = useMemoizedFn(
    (e: React.ChangeEvent<HTMLTextAreaElement>): boolean => {
      const resolved = resolveChangeContext(
        e.target.value,
        e.target.selectionStart || 0,
      );
      const newValue = resolved.value;
      const cursorPos = resolved.cursorPos;

      if (openLatest.current && triggerIndexRef.current >= 0) {
        setInputValue(newValue);
        const activeIdx = triggerIndexRef.current;
        const typedIdx = cursorPos > 0 ? cursorPos - 1 : -1;

        // 在其它位置输入了新的触发符（如正文中间 @），应切到新触发位
        if (
          typedIdx >= 0
          && typedIdx !== activeIdx
          && newValue.charAt(typedIdx) === triggerChar
        ) {
          triggerIndexRef.current = typedIdx;
          setMentionOpenReason(charOpenReason);
          setMentionQuery(extractMentionQuery(newValue, typedIdx));
          updateAnchorPosition(typedIdx);
          setDropdownOpen(true);
          return true;
        }

        if (
          activeIdx < newValue.length
          && newValue.charAt(activeIdx) === triggerChar
        ) {
          setMentionQuery(extractMentionQuery(newValue, activeIdx));
          updateAnchorPosition(activeIdx);
          return true;
        }

        setDropdownOpen(false);
        triggerIndexRef.current = -1;
        setMentionQuery('');

        if (tryOpenByTrigger(newValue, cursorPos)) {
          return true;
        }
        return true;
      }

      if (tryOpenByTrigger(newValue, cursorPos)) {
        setInputValue(newValue);
        return true;
      }

      return false;
    },
  );

  const openDropdownAsButton = useMemoizedFn(() => {
    triggerIndexRef.current = -1;
    setMentionQuery('');
    setMentionOpenReason('button');
    setDropdownOpen(true);
  });

  const onDropdownOpenChange = useMemoizedFn((open: boolean) => {
    if (open) {
      setMentionOpenReason('button');
      setDropdownOpen(true);
      return;
    }
    setMentionQuery('');
    if (clearIndexOnClose) {
      triggerIndexRef.current = -1;
    }
    setMentionOpenReason('button');
    setDropdownOpen(false);
  });

  const cleanupTriggerText = useMemoizedFn((
    insertLabel?: string,
    insertOptions?: IInsertMentionOptions,
    behavior?: ICleanupTriggerBehavior,
  ) => {
    const shouldClose = behavior?.closeDropdown !== false;
    const forceSelection = behavior?.insertAtSelection === true;

    if (!insertLabel && triggerIndexRef.current < 0) return;

    const surface = textareaRef.current;
    const savedTriggerIndex = triggerIndexRef.current;

    if (isMentionEditorSurface(surface)) {
      surface.focus();
      const kind = triggerChar === '@' ? 'knowledge' : 'skill';
      const body = insertLabel
        ? parseMentionBodyFromLabel(insertLabel, kind)
        : '';
      const mentionKey = kind === 'knowledge' && insertOptions?.knowledgeItems?.length
        ? buildKnowledgeMentionKey(insertOptions.knowledgeItems)
        : `${kind}:${Date.now()}`;
      if (forceSelection && body) {
        surface.insertMentionAtSelection(
          body,
          kind,
          mentionKey,
          insertOptions,
        );
      } else if (savedTriggerIndex < 0 && body) {
        const serialized = surface.value || '';
        const sel = surface.selectionStart ?? serialized.length;
        const resolvedIndex = resolveMentionTriggerIndex(
          serialized,
          triggerChar,
          -1,
          sel,
        );
        if (resolvedIndex >= 0) {
          surface.insertMentionAtTrigger(
            resolvedIndex,
            body,
            kind,
            mentionKey,
            triggerChar,
            insertOptions,
          );
        } else {
          surface.insertMentionAtSelection(
            body,
            kind,
            mentionKey,
            insertOptions,
          );
        }
      } else {
        surface.insertMentionAtTrigger(
          savedTriggerIndex,
          body,
          kind,
          mentionKey,
          triggerChar,
          insertOptions,
        );
      }
      triggerIndexRef.current = -1;
      setMentionQuery('');
      programmaticFocusRef.current = true;
      if (shouldClose) {
        setDropdownOpen(false);
      }
      return;
    }

    const textarea = surface as HTMLTextAreaElement | null;
    if (textarea && document.activeElement !== textarea) {
      textarea.focus();
    }

    const selectionStart = textarea?.selectionStart;
    const selectionEnd = textarea?.selectionEnd;

    setInputValue((prev) => {
      const current = prev || '';
      const { nextValue, cursor } = replaceTriggerOrInsertAt(
        current,
        insertLabel || '',
        {
          triggerIndex: forceSelection ? -1 : savedTriggerIndex,
          selectionStart: selectionStart ?? current.length,
          selectionEnd: selectionEnd ?? current.length,
        },
      );
      pendingCursorRef.current = cursor;
      return nextValue;
    });

    triggerIndexRef.current = -1;
    setMentionQuery('');
    programmaticFocusRef.current = true;
    if (shouldClose) {
      setDropdownOpen(false);
    }
    if (textarea && document.activeElement !== textarea) {
      textarea.focus();
    }
  });

  useLayoutEffect(() => {
    const cursor = pendingCursorRef.current;
    const el = textareaRef.current;
    if (cursor === null || !el) return;
    pendingCursorRef.current = null;
    if (isMentionEditorSurface(el)) {
      el.setSelectionRange(cursor, cursor);
    } else {
      el.selectionStart = cursor;
      el.selectionEnd = cursor;
    }
    programmaticFocusRef.current = false;
  }, [inputValue, textareaRef]);

  // @ / 模式下：打开或 query 变化时刷新虚拟锚点，避免第二次触发沿用旧坐标
  useLayoutEffect(() => {
    if (!dropdownOpen || mentionOpenReason !== charOpenReason) return;
    const idx = triggerIndexRef.current;
    if (idx < 0) return;
    updateAnchorPosition(idx);
    const rafId = requestAnimationFrame(() => {
      updateAnchorPosition(idx);
    });
    return () => cancelAnimationFrame(rafId);
  }, [
    dropdownOpen,
    mentionOpenReason,
    mentionQuery,
    inputValue,
    charOpenReason,
    updateAnchorPosition,
  ]);

  const handleTextareaFocus = useMemoizedFn(() => {
    if (programmaticFocusRef.current) {
      programmaticFocusRef.current = false;
      return;
    }
    setTimeout(() => {
      if (openLatest.current) return;
      const surface = textareaRef.current;
      if (!surface) return;
      const cursorPos = isMentionEditorSurface(surface)
        ? surface.selectionStart
        : surface.selectionStart ?? 0;
      tryOpenByTrigger(inputLatest.current || '', cursorPos);
    }, 0);
  });

  useEffect(() => {
    return () => {
      if (anchorRef.current) {
        document.body.removeChild(anchorRef.current);
        anchorRef.current = null;
      }
    };
  }, []);

  return {
    dropdownOpen,
    mentionQuery,
    mentionOpenReason,
    mentionAnchorRef: anchorRef,
    onDropdownOpenChange,
    openDropdownAsButton,
    wrapTextareaChange,
    isCharModeOpen: dropdownOpen && mentionOpenReason === charOpenReason,
    cleanupTriggerText,
    handleTextareaFocus,
  };
}
